import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import { useEffect } from "react";
import { PageHero } from "@/components/page-hero";
import { CTASection } from "@/components/cta-section";
import { InternalLinksHub } from "@/components/internal-links-hub";
import { ALL_AUTHORS } from "@/content/authors";
import { blogPosts } from "@/content/blog";
import annieWardPortrait from "@/assets/team/annie-ward.png.asset.json";
import davidWhitePortrait from "@/assets/team/david-white.png.asset.json";
import elizabethCavendishPortrait from "@/assets/team/elizabeth-rebecca-cavendish.png.asset.json";
import lianneSaundersPortrait from "@/assets/team/lianne-saunders.png.asset.json";

const TEAM_PORTRAITS: Record<string, { src: string; position: string }> = {
  "david-white": { src: davidWhitePortrait.url, position: "object-center" },
  "annie-ward": { src: annieWardPortrait.url, position: "object-center" },
  "lianne-saunders": { src: lianneSaundersPortrait.url, position: "object-center" },
  "elizabeth-cavendish": { src: elizabethCavendishPortrait.url, position: "object-center" },
};

export const Route = createFileRoute("/about-employerhq")({
  head: () => ({
    meta: [
      { title: "About Us, Senior HR & Employment Law Experts" },
      {
        name: "description",
        content:
          "Meet the senior HR advisors and SRA-regulated employment solicitors protecting UK SMEs since 2011.",
      },
      { property: "og:title", content: "About Us, Senior HR & Employment Law Experts" },
      { property: "og:description", content: "Senior HR & employment law experts for ambitious UK businesses." },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/about-employerhq" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/about-employerhq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About HR Service Centre",
          url: "https://www.hrservicecentre.co.uk/about-employerhq",
          description:
            "Meet the senior HR advisors and SRA-regulated employment solicitors behind HR Service Centre, protecting UK SMEs since 2011.",
          mainEntity: { "@id": "https://www.hrservicecentre.co.uk/#organization" },
          isPartOf: { "@id": "https://www.hrservicecentre.co.uk/#website" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "About", item: "https://www.hrservicecentre.co.uk/about-employerhq" },
          ],
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  const href = useLocation({ select: (location) => location.href });
  const articlesByAuthor = new Map(
    ALL_AUTHORS.map((author) => [author.name, blogPosts.filter((post) => post.author === author.name)]),
  );

  useEffect(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: "start" }));
  }, [href]);

  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "About", path: "/about-employerhq" },
        ]}
        eyebrow="About us"
        title="About HR Service Centre, UK HR & Employment Law Consultancy"
        subtitle="The senior HR and employment law team behind HR Service Centre."
        intro="HR Service Centre was founded in 2011 to give UK SMEs the kind of senior HR and legal support that, until then, was only available to large corporates. Today we work with 1,200+ businesses across every UK sector."
      />

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 sm:grid-cols-3">
          {[
            { k: "Founded", v: "2011" },
            { k: "Senior advisors", v: "28" },
            { k: "Average client tenure", v: "6.4 years" },
          ].map((s) => (
            <div key={s.k} className="rounded-2xl border border-border bg-card p-8 text-center">
              <p className="font-serif text-4xl text-brand">{s.v}</p>
              <p className="mt-2 text-sm text-muted-foreground">{s.k}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-card/40 px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-serif text-3xl text-brand">HR &amp; employment law client support consultants</h2>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2">
            {ALL_AUTHORS.map((m) => (
              <article
                key={m.slug}
                id={`author-${m.slug}`}
                className="scroll-mt-28 rounded-2xl bg-card p-8 ring-1 ring-black/5"
              >
                {TEAM_PORTRAITS[m.slug] ? (
                  <div className="group/portrait relative size-24 overflow-hidden rounded-full bg-secondary ring-4 ring-background shadow-sm motion-safe:animate-[portrait-float_6s_ease-in-out_infinite]">
                    <img
                      src={TEAM_PORTRAITS[m.slug].src}
                      alt={`Illustrated portrait of ${m.name}`}
                      title={`${m.name}, ${m.jobTitle}`}
                      width={768}
                      height={768}
                      loading="lazy"
                      decoding="async"
                      className={`size-full object-cover ${TEAM_PORTRAITS[m.slug].position} transition-transform duration-500 group-hover/portrait:scale-105`}
                    />
                  </div>
                ) : null}
                <h3 className="mt-6 font-serif text-xl text-brand">{m.name}</h3>
                <p className="text-sm text-accent">{m.jobTitle}</p>
                <p className="mt-1 text-xs text-muted-foreground">{m.credentials}</p>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{m.bio}</p>
                {(articlesByAuthor.get(m.name)?.length ?? 0) > 0 && (
                  <div className="mt-6 border-t border-border pt-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Articles by {m.name}
                    </p>
                    <ul className="mt-3 space-y-2">
                      {articlesByAuthor.get(m.name)?.map((post) => (
                        <li key={post.slug}>
                          <Link
                            to="/hr-insights/$slug"
                            params={{ slug: post.slug }}
                            className="text-sm font-semibold leading-snug text-brand underline decoration-accent/40 underline-offset-4 hover:text-accent"
                          >
                            {post.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-serif text-3xl text-brand">What we believe</h2>
          <div className="mt-8 space-y-6 text-base leading-relaxed text-foreground/90">
            <p>
              HR done well is a competitive advantage. HR done badly is one tribunal claim away from disaster. We sit in
              the middle, preventing problems where we can, handling them properly where we can't.
            </p>
            <p>
              We don't believe in legalese or in long contracts. Every retainer is monthly rolling. Every piece of advice
              is in plain English. And we'll always tell you straight when something is a bad idea, even if it costs us
              the work.
            </p>
          </div>
        </div>
      </section>


      <InternalLinksHub heading="Discover how HR Service Centre supports UK employers" />
      <CTASection />
    </>
  );
}

